import { type FC, useEffect, useRef, useState } from "react";
import { Input } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { PhoneNumber } from "../../../schema";

export interface NotificationContactPhoneFieldProps {
  /** `null` if the user has never set one. */
  readonly phoneNumber: string | null;
  /** Called on blur with a validated phone number, or `null` to clear — not called if the field is unchanged. */
  readonly onSave: (phoneNumber: string | null) => void;
}

const INVALID_FORMAT_ERROR = "Enter a phone number in international format, e.g. +61123456789";

/**
 * SMS delivery reads only from this field, never from the account's Auth0
 * profile (which is essentially never populated for this tenant base) — see
 * `NotificationContactInfoService`. A user with no phone number on file can
 * still receive email notifications; SMS for any category just has nothing
 * to send to until this is set.
 */
export const NotificationContactPhoneField: FC<NotificationContactPhoneFieldProps> = ({ phoneNumber, onSave }) => {
  const [value, setValue] = useState(phoneNumber ?? "");
  const [error, setError] = useState<string | undefined>(undefined);
  // Refs, not state: reading focus/clear status must not itself trigger the
  // resync effect below (a plain `isFocused` state dependency would re-run
  // the effect on every blur, snapping the field back to the last-known
  // `phoneNumber` before the just-triggered save has round-tripped).
  const isFocusedRef = useRef(false);
  const justClearedRef = useRef(false);

  // `phoneNumber` typically arrives asynchronously (a still-in-flight query
  // when this mounts, or a save round-tripping) — resync whenever it
  // changes, but never while the user is actively focused in the field, so
  // an in-progress edit can't be clobbered by a background refetch.
  useEffect(() => {
    if (!isFocusedRef.current) setValue(phoneNumber ?? "");
  }, [phoneNumber]);

  const handleChangeText = (text: string) => {
    setValue(text);
    if (error !== undefined) setError(undefined);
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    const trimmed = value.trim();

    // On web, pressing the clear button blurs the input *before* the button's
    // own press handler runs — deferring this by a tick lets handleClear's
    // synchronous flag win the race, so a clear-then-blur sequence saves
    // `null` exactly once rather than both the pre-clear text and `null`.
    setTimeout(() => {
      if (justClearedRef.current) {
        justClearedRef.current = false;
        return;
      }

      if (trimmed === (phoneNumber ?? "")) return;

      if (trimmed === "") {
        setError(undefined);
        onSave(null);
        return;
      }

      const result = PhoneNumber.safeParse(trimmed);
      if (!result.success) {
        setError(INVALID_FORMAT_ERROR);
        return;
      }

      setError(undefined);
      onSave(result.data);
    }, 0);
  };

  const handleClear = () => {
    justClearedRef.current = true;
    setValue("");
    setError(undefined);
    onSave(null);

    // On platforms/paths where clear doesn't also blur the input (e.g.
    // native, where tapping the clear icon doesn't dismiss the keyboard),
    // there's no handleBlur timeout to consume this flag. Left set, it
    // would linger and cause some later, unrelated blur (after the user
    // types a new number) to wrongly skip its save. Reset it on the same
    // tick handleBlur's own timeout uses, so it only ever survives long
    // enough to coalesce a blur that's actually part of this clear.
    setTimeout(() => {
      justClearedRef.current = false;
    }, 0);
  };

  return (
    <Input
      label="Phone number for SMS"
      value={value}
      onChangeText={handleChangeText}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={handleBlur}
      onClear={handleClear}
      placeholder="+61123456789"
      error={error}
      helperText={error === undefined ? "Required to receive SMS notifications." : undefined}
      clearable
      accessibilityLabel="Phone number for SMS notifications"
    />
  );
};
